import { useState } from "react";
import { Button, EmptyState, Sheet } from "../../../design-v2";

/*
 * What /ai shows instead of the conversation: the consent screen (D4) and the
 * states where the coach cannot answer. Export and erase stay reachable from
 * every one of them: they are rights, not coach features (contract §2.2).
 */

// The full terms the athlete agrees to (D4). Sign-up has no terms or privacy
// policy of its own, so everything the coach does with data is stated here:
// behind "Privacy details" on the activation screen, and again under
// Settings > Data controls > Consent.
export function ConsentDetails() {
  return (
    <ul className="lz-coach-consent__details">
      <li>Replies are written by an AI model (Claude, by Anthropic). Your coach can be wrong, and it isn't medical advice.</li>
      <li>To answer, it reads your Training Profile, your published program, your workout history and your recent check-ins.</li>
      <li>Your messages and weekly check-ins are kept. Facts you state about yourself (habits, goals, constraints) are remembered without asking each time; guesses are never saved.</li>
      <li>When you ask “do you remember…”, it searches your past messages and memories. This is on by default; turn it off in Coach settings, under Personalization.</li>
      <li>Health details, pain, body weight and mood are never kept as memories. Pain belongs in Pain issues.</li>
      <li>It only changes your program or your Training Profile with your approval.</li>
      <li>Coach data is kept for up to 24 months. You can export it, erase it, or stop using the coach at any time in Coach settings.</li>
    </ul>
  );
}

// One activation step. Enabling records the consent version (contract §2.3);
// nothing is assumed from having signed up.
export function CoachConsent({ consent, onAccept, onNotNow }) {
  const [status, setStatus] = useState("idle");
  const [detailsOpen, setDetailsOpen] = useState(false);

  async function accept() {
    setStatus("saving");
    const ok = await onAccept({ version: consent.currentVersion });
    if (!ok) setStatus("error");
  }

  return (
    <section className="lz-coach-gate" aria-labelledby="lz-coach-consent-title">
      <div className="lz-coach-consent">
        <CoachMark />
        <h2 id="lz-coach-consent-title" className="lz-coach-gate__title">Meet your AI Coach</h2>
        <p className="lz-coach-consent__lead">
          Your coach uses your Training Profile, your workouts and what you tell it to personalize its answers. It remembers
          the facts you share and only changes your program with your approval.
        </p>
        <button type="button" className="lz-coach-text-button lz-coach-consent__details-link" onClick={() => setDetailsOpen(true)}>
          Privacy details
        </button>
        {status === "error" ? (
          <p className="lz-coach-inline-error" role="alert">Couldn't enable the coach. Try again.</p>
        ) : null}
        <div className="lz-coach-consent__actions">
          <Button size="lg" className="lz-coach-gate__primary" isLoading={status === "saving"} loadingLabel="Enabling" onClick={accept}>
            Enable AI Coach
          </Button>
          <Button variant="ghost" className="lz-coach-gate__primary" onClick={onNotNow}>
            Not now
          </Button>
        </div>
      </div>
      <Sheet open={detailsOpen} onClose={() => setDetailsOpen(false)} title="Privacy details" surfaceClassName="lz-coach-sheet">
        <ConsentDetails />
      </Sheet>
    </section>
  );
}

function CoachMark() {
  return (
    <span className="lz-coach-consent__mark" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        <path d="M12 3.5 13.6 8.4 18.5 10 13.6 11.6 12 16.5 10.4 11.6 5.5 10 10.4 8.4Z" />
        <path d="M18 15.5 18.7 17.3 20.5 18 18.7 18.7 18 20.5 17.3 18.7 15.5 18 17.3 17.3Z" />
      </svg>
    </span>
  );
}

// After "Not now": nothing is recorded and nothing nags; one quiet way back.
export function CoachOff({ onEnable }) {
  return (
    <section className="lz-coach-gate">
      <EmptyState
        title="AI Coach is off"
        body="Turn it on whenever you want answers based on your training."
        action={<Button variant="secondary" onClick={onEnable}>Enable AI Coach</Button>}
      />
    </section>
  );
}

const UNAVAILABLE_COPY = {
  NOT_AUTHORIZED: {
    title: "Coach is in private pilot",
    body: "The coach is open to a few accounts for now. It will come to yours soon.",
  },
  DISABLED: {
    title: "Coach is off for now",
    body: "The coach isn't available at the moment. Your training works as usual.",
  },
  UNAVAILABLE: {
    title: "Coach is temporarily unavailable",
    body: "Something is off on our side. Try again in a few minutes.",
    retry: true,
  },
  ERROR: {
    title: "Couldn't reach your coach",
    body: "Check your connection and try again.",
    retry: true,
  },
};

export function CoachUnavailable({ availability, onRetry, onManageData }) {
  const copy = UNAVAILABLE_COPY[availability] || UNAVAILABLE_COPY.ERROR;
  return (
    <section className="lz-coach-gate">
      <EmptyState
        title={copy.title}
        body={copy.body}
        action={
          <div className="lz-coach-gate__actions">
            {copy.retry ? (
              <Button variant="secondary" onClick={onRetry}>
                Try again
              </Button>
            ) : null}
            {onManageData ? (
              <button type="button" className="lz-coach-text-button" onClick={onManageData}>
                Export or erase my coach data
              </button>
            ) : null}
          </div>
        }
      />
    </section>
  );
}
